/**
 * HelpImproveMyaeCard — Community Training Programme entry point.
 *
 * A small dashboard card that lets students voluntarily contribute their
 * Turnitin Similarity Report to help improve MyAE. Opens a modal that
 * requires explicit consent and shows the configurable reward. On success,
 * the reward (e.g. 30 days Pro + badge) is auto-issued.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Brain, Upload, X, Loader2, Check, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("ae_token")}` } });


export default function HelpImproveMyaeCard() {
  const [prog, setProg] = useState(null);
  const [open, setOpen] = useState(false);
  const [consent, setConsent] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/community/turnitin-programme`, authHeaders())
      .then((r) => setProg(r.data))
      .catch(() => setProg({ enabled: false }));
  }, []);

  if (!prog || !prog.enabled) return null;

  const submit = async () => {
    if (!file) { toast.error("Choose a Turnitin PDF."); return; }
    if (!consent) { toast.error("Please confirm consent."); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("consent", "true");
    try {
      const r = await axios.post(`${API}/community/turnitin-contribution`, fd, authHeaders());
      setResult(r.data);
      toast.success("Thank you — your contribution has helped improve MyAE.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setOpen(false); setFile(null); setConsent(false); setResult(null);
  };

  return (
    <>
      {/* Dashboard card */}
      <div className="relative overflow-hidden border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-amber-50 p-5 group" data-testid="help-improve-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-600 to-amber-500 flex items-center justify-center text-white shrink-0">
            <Brain size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-semibold text-indigo-700">Community Training Programme</p>
            <h3 className="font-serif text-lg text-zinc-900 mt-0.5">Help Improve MyAE</h3>
            <p className="text-xs text-zinc-600 mt-1 leading-relaxed">
              Received your university&apos;s Turnitin Similarity Report? Upload it to help us calibrate MyAE&apos;s similarity engine for thousands of students.
            </p>
            <p className="text-[11px] text-indigo-700 mt-1.5 font-medium">
              🎁 Thank-you reward: <span className="font-semibold">{prog.reward_summary}</span>
            </p>
            <button
              onClick={() => setOpen(true)}
              data-testid="help-improve-open"
              className="mt-3 inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs px-3 py-1.5 font-semibold transition-colors"
            >
              <Upload size={12} /> Upload my report
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={reset} data-testid="help-improve-modal">
          <div className="bg-white w-full max-w-lg my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
              <div className="flex items-center gap-2"><Brain size={16} className="text-indigo-600" /><p className="font-serif text-lg text-zinc-900">Help Improve MyAE</p></div>
              <button onClick={reset} className="text-zinc-400 hover:text-zinc-900" data-testid="help-improve-close"><X size={18} /></button>
            </div>

            {!result ? (
              <div className="p-5">
                <p className="text-sm text-zinc-700 mb-4">
                  Upload your <span className="font-semibold">Turnitin Similarity Report PDF</span> — the file you downloaded from your university&apos;s submission portal after your assignment was graded.
                </p>

                {/* File picker */}
                <label className="block mb-4">
                  <span className="text-xs uppercase tracking-wider text-zinc-500">Turnitin PDF</span>
                  <input
                    type="file" accept=".pdf"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    data-testid="help-improve-file"
                    className="mt-1 block w-full text-sm border border-zinc-200 p-2 file:mr-3 file:border-0 file:bg-zinc-100 file:px-3 file:py-1 file:text-xs file:font-medium hover:file:bg-zinc-200"
                  />
                  {file && <p className="text-[11px] text-emerald-700 mt-1">✓ {file.name} ({(file.size / 1024).toFixed(0)} KB)</p>}
                </label>

                {/* Consent — VERBATIM from admin config, cannot be pre-checked */}
                <label className="flex items-start gap-2 p-3 bg-zinc-50 border border-zinc-200 mb-4 cursor-pointer">
                  <input
                    type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                    data-testid="help-improve-consent"
                    className="mt-1 shrink-0"
                  />
                  <span className="text-xs text-zinc-700 leading-relaxed">{prog.consent_text}</span>
                </label>

                {/* Reward preview */}
                <div className="bg-gradient-to-r from-indigo-50 to-amber-50 border border-indigo-200 p-3 mb-4">
                  <p className="text-[10px] uppercase tracking-widest text-indigo-700 font-semibold">Your thank-you reward</p>
                  <p className="text-sm text-zinc-900 mt-0.5 font-medium">🎁 {prog.reward_summary}</p>
                </div>

                <div className="flex justify-end gap-2">
                  <button onClick={reset} className="text-sm px-4 py-1.5 border border-zinc-300 hover:bg-zinc-50">Cancel</button>
                  <button
                    onClick={submit}
                    disabled={uploading || !file || !consent}
                    data-testid="help-improve-submit"
                    className="text-sm px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white flex items-center gap-1.5 font-semibold"
                  >
                    {uploading ? (<><Loader2 size={13} className="animate-spin" /> Uploading…</>) : (<><Upload size={13} /> Contribute report</>)}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-5 text-center" data-testid="help-improve-success">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <Check size={28} className="text-emerald-600" />
                </div>
                <p className="font-serif text-2xl text-zinc-900 mb-1">Thank you!</p>
                <p className="text-sm text-zinc-600 mb-4">
                  Your contribution helped calibrate MyAE. Turnitin scored this <span className="font-semibold">{result.benchmark?.turnitin_pct}%</span>.
                </p>
                <div className="bg-gradient-to-r from-indigo-50 to-amber-50 border border-indigo-200 p-4 mb-4 text-left">
                  <p className="text-[10px] uppercase tracking-widest text-indigo-700 font-semibold flex items-center gap-1"><Sparkles size={11} /> Reward issued</p>
                  {result.reward?.days && <p className="text-sm text-zinc-900 mt-1">✅ {result.reward.days} days of MyAE Pro added</p>}
                  {result.reward?.credits && <p className="text-sm text-zinc-900 mt-1">✅ {result.reward.credits} analysis credit(s) added</p>}
                  {result.reward?.badge && <p className="text-sm text-zinc-900 mt-1">🏅 &ldquo;{result.reward.badge}&rdquo; badge unlocked</p>}
                </div>
                <button onClick={reset} className="text-sm px-4 py-2 bg-oxford text-white hover:bg-[#2C5282]" data-testid="help-improve-done">Done</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
